import { useEffect, useRef, useState } from 'react';
import { useCopilot } from '../../api/copilot';
import { callSafely, describeValue, humanizeKey } from './util';

/** Statuses in which the question is still open and the buttons still mean something. */
const OPEN = new Set([undefined, null, '', 'confirm', 'waiting']);
/* An inputs summary is a check, not a form: past this many rows the reader
   stops reading, and the agent's own sentence already names the subject. */
const MAX_INPUTS = 6;

/**
 * Ask before the agent writes or runs something expensive.
 *
 * The agent proposes; the person at the desk disposes. The card states what
 * will run (title), why or on what (detail), the inputs it will run with, and
 * offers Run and Cancel. Nothing runs until Run is pressed — the provider holds
 * the request open until `respond` resolves it, and lets it expire after two
 * minutes so a card forgotten in the scrollback cannot be approved an hour
 * later against a different screen.
 *
 * The buttons disable on the first press, before the provider has updated the
 * card, so a double click cannot answer twice; the answer given is shown until
 * the provider's own status replaces it.
 *
 * Answering replaces the buttons with the outcome, which would drop keyboard
 * focus to the page body — the top of the document for the next Tab and for a
 * screen reader. So when focus was on a button as they go, it moves to the
 * card itself. Only then: the dock and the drawer render the same card, and
 * the copy nobody pressed must not pull focus to itself.
 */
export default function ConfirmActionCard({ message }) {
  const { respond } = useCopilot();
  const [answer, setAnswer] = useState(null);
  const cardRef = useRef(null);
  const focusInside = useRef(false);
  const card = message?.card;
  const status = card?.status;
  const open = OPEN.has(status) && answer === null;

  useEffect(() => {
    if (open || !focusInside.current) return;
    focusInside.current = false;
    const active = typeof document !== 'undefined' ? document.activeElement : null;
    if (!active || active === document.body) cardRef.current?.focus();
  }, [open]);

  if (!card) return null;
  const inputs = Object.entries(card.inputs && typeof card.inputs === 'object' ? card.inputs : {})
    .map(([k, v]) => [humanizeKey(k), describeValue(v)])
    .filter(([, v]) => v)
    .slice(0, MAX_INPUTS);

  const answerWith = (yes) => {
    if (!open) return;
    /* The button is about to leave the DOM, and not every browser reports a
       blur for a removed node, so the press itself marks focus as inside. */
    focusInside.current = true;
    setAnswer(yes ? 'yes' : 'no');
    callSafely(respond, message.id, yes);
  };

  let outcome = null;
  if (!open) {
    if (status === 'cancelled' || (OPEN.has(status) && answer === 'no')) outcome = 'Cancelled';
    else if (status === 'expired') outcome = 'Expired — ask again if you still want it';
    else if (status === 'failed') outcome = 'Failed';
    else outcome = 'Confirmed';
  }

  return (
    <div
      className={`cp-card cp-confirm${open ? ' open' : ''}`}
      role="group"
      aria-label={`Confirm: ${card.title || 'agent action'}`}
      ref={cardRef}
      tabIndex={-1}
      onFocus={() => { focusInside.current = true; }}
      onBlur={(e) => {
        focusInside.current = Boolean(e.relatedTarget && e.currentTarget.contains(e.relatedTarget));
      }}
    >
      <b className="cp-card-title">{card.title || 'Run this action?'}</b>
      {card.detail && <p className="cp-card-detail">{card.detail}</p>}
      {inputs.length > 0 && (
        <dl className="cp-inputs">
          {inputs.map(([k, v]) => (
            <div key={k}>
              <dt>{k}</dt>
              <dd>{v}</dd>
            </div>
          ))}
        </dl>
      )}
      {open ? (
        <>
          <p className="cp-card-q">{card.question || 'The assistant wants to run this. Go ahead?'}</p>
          <div className="cp-card-actions">
            <button type="button" className="btn pri cp-btn" onClick={() => answerWith(true)}>
              Run
            </button>
            <button type="button" className="btn cp-btn" onClick={() => answerWith(false)}>
              Cancel
            </button>
          </div>
        </>
      ) : (
        <p className={`cp-card-outcome${outcome === 'Confirmed' ? ' ok' : ''}`} role="status" aria-live="polite">
          {outcome}
        </p>
      )}
    </div>
  );
}
